import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Image,
  Form,
  Button,
  Table,
  FormCheck,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Formik, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import InputField from "../../../components/input/input-field";
import avatars1 from "../../../assets/images/avatars/01.png";
import CustomPhoneInput from "../../../components/input/phone-input";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import FocusError from "../../../components/error/error-focus";
import "react-phone-input-2/lib/style.css";
import FadeLoaderComponent from "../../../components/loader-component";
import { roleTypes } from "../../../data/roleType";

const WarehouseAccountView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const alertRef = useRef(null);
  const alertPermissionRef = useRef();
  const [loader, setLoader] = useState(true);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [userWarehousesRolesPermissions, setUserWarehousesRolesPermissions] =
    useState();
  const [user, setUser] = useState();
  const [userProfilePicture, setUserProfilePicture] = useState();
  const [permissions, setPermissions] = useState([]);
  const token = localStorage.getItem("coyote-token");
  const [decodedToken, setDecodedToken] = useState();

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getPermissions = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}permission/active-user-permissions`,
        config
      );
      return response.data.response.data;
    } catch (err) {
      handleErrorPermission(err, "permission-edit");
    }
  };
  const allowedActions = ["create", "update", "view", "delete", "status"];

  const getRoles = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}warehouse/role/all`,
        config
      );
      setRoles(response.data.response.data);
      return response.data.response.data;
    } catch (err) {
      handleError(err, "user-edit");
    }
  };

  const getUserData = async () => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type)
      )
    ) {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };
        const response = await axios.get(
          `${url.backendURL}warehouse/user/${id}`,
          config
        );

        const userData = response.data.response.data;
        const { profilePicture, warehouseRoles, ...otherData } = userData;
        const allPermissions = await getPermissions();
        const initialValues = {};

        allPermissions?.forEach((permission) => {
          const slug = permission.slug;
          initialValues[slug] = {};
          const validActions = permission.permissions.filter((action) =>
            allowedActions.includes(action)
          );

          validActions.forEach((action) => {
            initialValues[slug][action] = false;
          });
        });
        await getRoles();
        const userRolesWarehousesPermissions = warehouseRoles
          ? {
              warehouseId: warehouseRoles.warehouseId || null,
              isWarehouseHead: warehouseRoles.isWarehouseHead || 0,
              roles: Array.isArray(warehouseRoles.roles)
                ? warehouseRoles.roles
                : [], // Ensure roles is an array
              permissions: {
                ...initialValues,
                ...warehouseRoles.permissions, // Merge default & existing permissions safely
              },
            }
          : {
              warehouseId: null,
              isWarehouseHead: 0,
              roles: [],
              permissions: { ...initialValues }, // Use only default permissions
            };

        setPermissions(allPermissions); // Update all permissions
        setLoader(false);
        setUserProfilePicture(profilePicture); // Update profile picture
        setUserWarehousesRolesPermissions(userRolesWarehousesPermissions || {});
        setUser({ ...otherData, userRolesWarehousesPermissions });
      } catch (err) {
        handleError(err, "user-edit");
        setLoader(false);
      }
    }
  };

  useEffect(() => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type)
      )
    ) {
      getUserData();
    }
  }, [decodedToken, token]);

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleErrorPermission = (err, alertFor) => {
    const { response } = err;

    if (alertPermissionRef.current) {
      alertPermissionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const roleOptions =
      roles.length > 0 &&
      roles.map((role) => ({
        value: role.id,
        label: role.name,
      }));
    setOptions(roleOptions);
  }, [roles]);

  return (
    <>
      <div>
        <Formik
          enableReinitialize={true}
          initialValues={{
            phoneNumber: user?.phoneNumber || "",
            email: user?.email,
            name: user?.name,
            profilePicture: user?.profilePicture || null,
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <FocusError />
              <Row>
                <Col xl="3" lg="4" className="d-flex">
                  <div style={{ width: "100%", display: "grid" }}>
                    <Card>
                      <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                          <h4 className="card-title">Edit User</h4>
                        </div>
                      </Card.Header>

                      <Card.Body>
                        {user && !loader && (
                          <>
                            <Form.Group className="form-group">
                              <div className="profile-img-edit position-relative">
                                <Image
                                  className="theme-color-default-img profile-pic rounded avatar-100"
                                  src={
                                    values?.profilePicture
                                      ? URL.createObjectURL(
                                          values.profilePicture
                                        )
                                      : userProfilePicture
                                      ? userProfilePicture
                                      : avatars1
                                  }
                                  alt="profile-pic"
                                />
                              </div>
                              <div className="img-extension mt-3">
                                <div className="d-inline-block align-items-center">
                                  <span>Only</span> <Link to="#">.jpg</Link>{" "}
                                  <Link to="#">.png</Link>{" "}
                                  <Link to="#">.jpeg</Link> <span>allowed</span>
                                </div>
                              </div>
                            </Form.Group>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                </Col>

                <Col xl="9" lg="8" className="d-flex">
                  <div style={{ width: "100%", display: "grid" }}>
                    <Card>
                      <Card.Header className="d-flex justify-content-between">
                        <div
                          className="breadcrumb header-title"
                          style={{ cursor: "pointer" }}
                        >
                          <h4
                            onClick={() =>
                              navigate("/dashboard/app/warehouse/user-list")
                            }
                            className="text-primary card-title"
                            style={{ cursor: "pointer" }}
                          >
                            <span>User</span>
                          </h4>
                          <h4 className="mx-2 card-title">{">"}</h4>
                          <h4 className="card-title">User Information</h4>
                        </div>
                      </Card.Header>

                      <Card.Body>
                        {alert.alertFor === "user-edit" && (
                          <AlertNotificationSimple
                            type={alert.type}
                            message={alert.message}
                            show={alert.show}
                            onClose={() => setAlert({ ...alert, show: false })}
                          />
                        )}
                        {user && !loader && (
                          <div className="new-user-info">
                            <div className="row">
                              <InputField
                                className="col-md-6 form-group"
                                type="text"
                                name="name"
                                placeholder="Enter Name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                asterick={true}
                                disabled={true}
                                errorMessage={errors?.name}
                                label="Name"
                              />

                              <InputField
                                className="col-md-6 form-group"
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                asterick={true}
                                disabled={true}
                                errorMessage={errors?.email}
                                label="Email"
                              />

                              <Row>
                                <Form.Group className="col-md-6 form-group">
                                  <Form.Label htmlFor="phoneNumber">
                                    Phone Number
                                  </Form.Label>
                                  <CustomPhoneInput
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    disabled={true}
                                    value={values.phoneNumber}
                                    onBlur={handleBlur}
                                    placeholder="Enter phone number"
                                    country="us"
                                    isValid={(value, country) => {
                                      if (value.match(/12345/)) {
                                        return `Invalid value: ${value}, ${country.name}`;
                                      } else if (value.match(/1234/)) {
                                        return false;
                                      } else {
                                        return true;
                                      }
                                    }}
                                  />
                                </Form.Group>
                              </Row>
                            </div>
                          </div>
                        )}
                        {loader && <FadeLoaderComponent />}
                      </Card.Body>
                    </Card>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        {userWarehousesRolesPermissions && (
          <Formik
            enableReinitialize={true}
            initialValues={{
              ...userWarehousesRolesPermissions,
            }}
          >
            {({
              values,
              handleBlur,
              setFieldValue,

              isSubmitting,
            }) => (
              <Form>
                <FocusError />

                {!loader && (
                  <Row>
                    <Col xl="12" lg="12" className="d-flex">
                      <div style={{ width: "100%", display: "grid" }}>
                        <Card>
                          <Card.Header className="d-flex justify-content-between pb-3">
                            <div className="header-title">
                              <h4 className="card-title">
                                {decodedToken?.warehouse}
                              </h4>
                            </div>
                          </Card.Header>

                          <Card.Body>
                            {user && !loader && (
                              <div className="user-permission">
                                <div className="row">
                                  {alert.alertFor ===
                                    "user-permission-edit" && (
                                    <AlertNotificationSimple
                                      type={alert.type}
                                      message={alert.message}
                                      show={alert.show}
                                      onClose={() =>
                                        setAlert({
                                          ...alert,
                                          show: false,
                                        })
                                      }
                                    />
                                  )}
                                  <Row className="mb-3">
                                    <Col>
                                      <h5 className=" align-items-center mb-4">
                                        Roles{" "}
                                      </h5>
                                      <div className="d-flex flex-wrap gap-4">
                                        {options.length > 0 &&
                                          options?.map((role) => {
                                            const isChecked =
                                              values?.roles?.includes(
                                                role?.value
                                              );

                                            return (
                                              <div
                                                key={role?.value}
                                                className="role-checkbox-item"
                                              >
                                                <Form.Check
                                                  type="checkbox"
                                                  id={`role-${role?.value}`}
                                                  label={role?.label}
                                                  checked={isChecked}
                                                  disabled={true}
                                                  onChange={(value) => {
                                                    const isRoleBeingRemoved =
                                                      isChecked;
                                                    let updatedRoles =
                                                      isRoleBeingRemoved
                                                        ? values.roles.filter(
                                                            (r) =>
                                                              r !== role?.value
                                                          )
                                                        : [
                                                            ...values.roles,
                                                            role?.value,
                                                          ];
                                                    setFieldValue(
                                                      "roles",
                                                      updatedRoles
                                                    );
                                                  }}
                                                  onBlur={handleBlur}
                                                  className="mb-2"
                                                />
                                              </div>
                                            );
                                          })}
                                      </div>
                                    </Col>

                                    {/* <Col>
                                      <h5 className=" align-items-center mb-4">
                                        Main Head{" "}
                                      </h5>
                                      <div className="d-flex flex-wrap gap-4">
                                        <div className="role-checkbox-item">
                                          <Form.Check
                                            type="checkbox"
                                            id={`isWarehouseHead`}
                                            checked={values.isWarehouseHead}
                                            disabled={true}
                                            onBlur={handleBlur}
                                            className="mb-2"
                                          />
                                        </div>
                                      </div>
                                    </Col> */}
                                  </Row>
                                  <hr className="mt-4 mb-4" />

                                  <Row>
                                    <h5 className=" align-items-center mt-1 mb-4">
                                      Permissions{" "}
                                    </h5>
                                    {permissions.map((permission) => (
                                      <div
                                        key={permission.id}
                                        className="mb-4 mt-2"
                                      >
                                        <Form.Label
                                          htmlFor="permissions"
                                          className="text-black"
                                        >
                                          {permission.label}
                                        </Form.Label>
                                        <div className="d-flex gap-4 flex-wrap mt-2">
                                          {permission.permissions.map(
                                            (perm) => (
                                              <label
                                                key={perm.permission_slug}
                                                className="d-flex align-items-center"
                                              >
                                                <Field
                                                  type="checkbox"
                                                  className="form-check-input"
                                                  disabled={true}
                                                  name={`permissions.${permission.slug}.${perm.permission_slug}`}
                                                  checked={
                                                    values?.permissions?.[
                                                      permission.slug
                                                    ]?.[perm.permission_slug] ||
                                                    false
                                                  }
                                                />
                                                <span className="ms-2">
                                                  {perm.label}
                                                </span>
                                              </label>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </Row>
                                </div>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    </Col>
                  </Row>
                )}
              </Form>
            )}
          </Formik>
        )}
      </div>
    </>
  );
};

export default WarehouseAccountView;
